import { useCallback, useState } from 'react';
import axios from 'axios';
import { useSWRConfig } from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { postDataFetcher } from '@/utils/apis';

// 같은 상태 코드에 사유가 여럿이라 서버 문구로 가름 — 백엔드 ApprovalPartialRejectService 문구와 맞출 것
const SERVER_REASONS: Record<string, string> = {
  '이미 부분 반려된 품번입니다.':
    '이미 부분 반려된 품번입니다. 새로고침하면 최신 상태를 확인할 수 있습니다.',
  '남은 품번이 하나뿐이라 부분 반려할 수 없습니다. 문서 전체를 반려해 주세요.':
    '남은 품번이 하나뿐이라 부분 반려할 수 없습니다. 문서 전체를 반려해 주세요.',
  '진행중인 문서만 부분 반려할 수 있습니다.':
    '진행 중인 문서만 부분 반려할 수 있습니다. 새로고침 후 결재 상태를 확인해 주세요.',
};

/** 실패 응답 → 안내 문구(번역 key) */
export const getPartialRejectErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return '부분 반려를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }
  const serverMessage = error.response?.data?.message;
  if (typeof serverMessage === 'string' && SERVER_REASONS[serverMessage]) {
    return SERVER_REASONS[serverMessage];
  }
  switch (error.response?.status) {
    case 403:
      return '지금은 결재 차례가 아니어서 부분 반려할 수 없습니다. 새로고침 후 결재 상태를 확인해 주세요.';
    case 404:
      return '문서를 찾을 수 없습니다. 삭제되었거나 볼 수 없는 문서인지 확인해 주세요.';
    case 408:
      return 'GERP 응답이 늦어 결과를 확인하지 못했습니다. 새로고침해 반영 여부를 확인한 뒤 다시 시도해 주세요.';
    case 409:
      return '다른 결재 처리가 진행 중입니다. 잠시 후 다시 시도해 주세요.';
    case 422:
      return '이 품번은 지금 부분 반려할 수 없는 상태입니다. 새로고침 후 다시 확인해 주세요.';
    default:
      return '부분 반려를 처리하지 못했습니다. 잠시 후 다시 시도하고, 계속되면 담당자에게 문의해 주세요.';
  }
};

/**
 * GERP 결재 마스터 계열(121·122·123·128·129) 품번 하나 부분 반려.
 * 성공하면 제목·본문·결재 이력이 바뀌므로 이 문서 캐시를 모두 다시 부름
 */
const usePartialReject = (documentId: string) => {
  const { mutate } = useSWRConfig();
  const [submitting, setSubmitting] = useState(false);

  const partialReject = useCallback(
    async (itemId: string, reason: string) => {
      setSubmitting(true);
      const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
      try {
        await postDataFetcher(`${baseKey}/partial-reject`, { itemId, reason });
        await mutate((key) => {
          const head = Array.isArray(key) ? key[0] : key;
          return typeof head === 'string' && head.startsWith(baseKey);
        });
      } finally {
        setSubmitting(false);
      }
    },
    [documentId, mutate],
  );

  return { partialReject, submitting };
};

export default usePartialReject;
